import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { MContentHeader } from '../../molecules/MContentHeader/index';
import { MContentHeaderAlignment } from '../../molecules/MContentHeader/MContentHeader.types';
import { MCarouselControls } from '../../molecules/MCarouselControls/index';
import { MCarouselControlsType } from '../../molecules/MCarouselControls/MCarouselControls.types';
import { MSlot } from '../../molecules/MSlot/index';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import * as Styled from './OCardCarousel.styles';
import type { OCardCarouselProps } from './OCardCarousel.types';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export const OCardCarousel: React.FC<OCardCarouselProps> = ({
  desktopCards = '3',
  mContentHeader,
  mCarouselControls,
  pagePath = '',
  itemPath = '',
  isAuthorMode = false,
  cqItems = {},
  model = {},
  aemReactComponentMapping = {},
  className = '',
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const desktopCardsNum = parseInt(desktopCards, 10) || 3;

  // Get cqItemsOrder from model, similar to OAccordion pattern
  const cqItemsOrder = model?.cqItemsOrder || model?.[':itemsOrder'] || [];

  // Get ordered item keys - if cqItemsOrder exists, use it; otherwise use cqItems keys
  const itemsToRender = useMemo(() => {
    const allItemKeys = Object.keys(cqItems);

    if (cqItemsOrder.length > 0) {
      const orderedSet = new Set(cqItemsOrder);
      // Get items that are in cqItemsOrder, preserving order
      const orderedItems = cqItemsOrder.filter((key: string) => allItemKeys.includes(key));
      // Get remaining items not in cqItemsOrder
      const remainingItems = allItemKeys.filter(key => !orderedSet.has(key));
      return [...orderedItems, ...remainingItems];
    }

    return allItemKeys;
  }, [cqItemsOrder, cqItems]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 992);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSlidesPerView = () => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    return desktopCardsNum;
  };

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handleSlideChange = (swiper: SwiperType) => {
    setCurrentSlide(swiper.activeIndex);
  };

  const controlsType = mCarouselControls?.type === 'Bar' ? MCarouselControlsType.Bar : MCarouselControlsType.Dots;

  const imageOverlay = mCarouselControls?.imageOverlay === 'true' || mCarouselControls?.imageOverlay === true;

  return (
    <Styled.Wrapper className={className}>
      {mContentHeader && (
        <Styled.HeaderWrapper>
          <MContentHeader
            mContentHeader={{
              alignment:
                mContentHeader.alignment === 'Centered'
                  ? MContentHeaderAlignment.Centered
                  : MContentHeaderAlignment.Left,
              mTextBlockSimple: mContentHeader.mTextBlockSimple,
              mButton: mContentHeader.mButton,
              mButton2: mContentHeader.mButton2,
            }}
          />
        </Styled.HeaderWrapper>
      )}

      <Styled.CarouselContainer>
        <Styled.SwiperWrapper>
          <Swiper
            modules={[Navigation, Pagination, A11y]}
            spaceBetween={16}
            slidesPerView={getSlidesPerView()}
            onSwiper={(swiper: SwiperType) => {
              swiperRef.current = swiper;
              setTotalSlides(swiper.slides.length - desktopCardsNum + 1);
            }}
            onSlideChange={handleSlideChange}
            breakpoints={{
              320: {
                slidesPerView: 1,
                spaceBetween: 16,
              },
              768: {
                slidesPerView: 2,
                spaceBetween: 16,
              },
              992: {
                slidesPerView: desktopCardsNum,
                spaceBetween: 16,
              },
            }}
          >
            {itemsToRender.length > 0
              ? itemsToRender.map(key => {
                  const itemData = cqItems[key] || {};
                  const itemType = itemData[':type'] as string | undefined;

                  return (
                    <SwiperSlide key={key}>
                      <Styled.SlideContent>
                        {isAuthorMode ? (
                          <EditableComponent
                            pagePath={pagePath}
                            itemPath={`${itemPath}/${key}`}
                            {...(itemData as Record<string, unknown>)}
                          />
                        ) : itemType && aemReactComponentMapping[itemType] ? (
                          React.createElement(aemReactComponentMapping[itemType], {
                            ...(itemData as Record<string, unknown>),
                            pagePath,
                            itemPath: `${itemPath}/${key}`,
                            isAuthorMode,
                            aemReactComponentMapping,
                          })
                        ) : (
                          <div>Component type not found: {itemType}</div>
                        )}
                      </Styled.SlideContent>
                    </SwiperSlide>
                  );
                })
              : isAuthorMode && (
                  <SwiperSlide>
                    <Styled.SlideContent>
                      <MSlot
                        pagePath={pagePath}
                        itemPath={`${itemPath}`}
                        isAuthorMode={isAuthorMode}
                        cqItems={{}}
                        model={model}
                        aemReactComponentMapping={aemReactComponentMapping}
                      />
                    </Styled.SlideContent>
                  </SwiperSlide>
                )}
          </Swiper>
        </Styled.SwiperWrapper>

        {mCarouselControls && (
          <Styled.ControlsWrapper imageOverlay={imageOverlay}>
            <MCarouselControls
              type={controlsType}
              imageOverlay={imageOverlay}
              totalSlides={totalSlides}
              currentSlide={currentSlide}
              onPrev={handlePrev}
              onNext={handleNext}
            />
          </Styled.ControlsWrapper>
        )}
      </Styled.CarouselContainer>
    </Styled.Wrapper>
  );
};

export * from './OCardCarousel.types';
